import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

// Component for the confirmation dialog when there are agent authority errors
const AgentAuthorityConfirmDialog = ({
  isOpen,
  onOpenChange,
  onConfirm,
  policyId,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  policyId: string;
}) => {
  const navigate = useNavigate();
  const [hasReviewed, setHasReviewed] = useState(false);

  // Reset checkbox when dialog opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setHasReviewed(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="space-y-4">
          <DialogTitle className="text-center text-base font-semibold leading-relaxed">
            This policy was referred for review by an agent due to agent
            authority rules.
          </DialogTitle>
          <DialogDescription className="space-y-4 text-center">
            <p className="text-sm leading-relaxed text-gray-700">
              Please confirm you have reviewed the fields in this policy that
              triggered the Agent Authority rules and approve this transaction.
            </p>
            <Button
              variant="link"
              onClick={() => {
                navigate(
                  `/policy/${policyId}/policy-decisions?tab=agentAuthorityErrors`
                );
                handleOpenChange(false);
              }}
              className="h-auto p-0 text-sm font-medium text-purple-600 underline hover:text-purple-800"
            >
              Click here to review the triggered rules
            </Button>
          </DialogDescription>
        </DialogHeader>

        <div className="my-6 flex items-start gap-3 rounded-lg border border-gray-200 p-4">
          <Checkbox
            id="review-confirmation"
            checked={hasReviewed}
            onCheckedChange={(checked) => setHasReviewed(checked === true)}
            className="mt-1"
          />
          <Label
            htmlFor="review-confirmation"
            className="flex-1 cursor-pointer text-sm font-medium leading-relaxed text-gray-900"
          >
            I have reviewed the triggered rules and approve this transaction
          </Label>
        </div>

        <DialogFooter className="sm:justify-center">
          <Button
            onClick={() => {
              onConfirm();
              handleOpenChange(false);
            }}
            disabled={!hasReviewed}
            className="w-full bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-500"
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentAuthorityConfirmDialog;
